import { PrintWindowBlockedError } from '~/utils/PrintWindowBlockedError'
import { PrintWindowTimeoutError } from '~/utils/PrintWindowTimeoutError'

// A window that never finishes loading the PDF rejects after this delay; a window whose print
// dialog was requested is released after it even if "afterprint" never arrives.
const PRINT_CLEANUP_FALLBACK_MS = 60_000
const LOAD_POLL_INTERVAL_MS = 50

// True from the call until the print dialog has been requested (or the print failed).
let isPrintInFlight = false

// The popup keeps no "load" listener across its navigation from the initial about:blank, so
// completion is read from the popup's own location and document state, which stay same-origin
// for a blob URL. Settles when the PDF document is complete; rejects when the popup closes or
// the deadline passes.
const waitForPdfLoad = (popup: Window, url: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const settle = (finish: () => void) => {
      clearInterval(pollTimer)
      clearTimeout(deadlineTimer)
      finish()
    }
    const pollTimer = setInterval(() => {
      try {
        if (popup.closed) {
          settle(() => {
            reject(new PrintWindowTimeoutError())
          })
          return
        }
        const hasLoadedPdf = popup.location.href === url && popup.document.readyState === 'complete'
        if (hasLoadedPdf) settle(resolve)
      } catch (error: unknown) {
        settle(() => {
          reject(error instanceof Error ? error : new Error('The PDF window could not be read.'))
        })
      }
    }, LOAD_POLL_INTERVAL_MS)
    const deadlineTimer = setTimeout(() => {
      settle(() => {
        reject(new PrintWindowTimeoutError())
      })
    }, PRINT_CLEANUP_FALLBACK_MS)
  })

/**
 * Prints the PDF that `loadPdf` returns through the browser print dialog. The popup opens
 * synchronously, before `loadPdf` is awaited, so it counts as opened by the user's click;
 * rejects with PrintWindowBlockedError, without calling `loadPdf`, when the browser blocks it.
 * Resolves once the print dialog has been requested; rejects, after closing the popup and
 * revoking the blob URL, when the load fails, the popup closes, or printing throws. The popup
 * and URL are released on "afterprint", or after a fallback delay. A call made while a previous
 * print is still loading resolves without printing.
 */
export const printPdfBlob = async (loadPdf: () => Promise<Blob>): Promise<void> => {
  if (isPrintInFlight) return
  const popup = window.open('', '_blank')
  if (popup === null) throw new PrintWindowBlockedError()
  isPrintInFlight = true

  let url = ''
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined
  const release = () => {
    clearTimeout(fallbackTimer)
    if (url !== '') URL.revokeObjectURL(url)
    popup.close()
  }

  try {
    const blob = await loadPdf()
    url = URL.createObjectURL(blob)
    popup.location.href = url
    await waitForPdfLoad(popup, url)
    popup.addEventListener('afterprint', release, { once: true })
    popup.focus()
    popup.print()
    fallbackTimer = setTimeout(release, PRINT_CLEANUP_FALLBACK_MS)
  } catch (error: unknown) {
    release()
    throw error
  } finally {
    isPrintInFlight = false
  }
}
